import {useTranslation} from 'react-i18next';
import './templates.professional-caregiver.scss';
import {useMemo} from 'react';
import {EntityTemplate} from '../../../../../../backend/responses/entity-template';
import {
  Paper,
  Skeleton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  tableCellClasses,
} from '@mui/material';
import {ContentCopyOutlined} from '@mui/icons-material';
import {FiTrash2, FiEdit} from 'react-icons/fi';
import {translations} from '../../../../../../i18n/translations';
import {tableSx} from '../../../../../../theme/table-sx';
import moment from 'moment';
import {TextCell} from '../../../../../../components/task-table/cells';
import {getActivityNameByActivitySectorId} from '../../../../../../utils/functions/get-activity-name-by-section';
import {ComboActions} from '../../../../../../components/combo-actions/combo-actions';

type ColumnsId = 'name' | 'area' | 'description' | 'update' | 'buttons';

type TemplatesDataProps = {
  data?: EntityTemplate[];
  isLoading: boolean;
  canWrite: boolean;
  duplicated?: EntityTemplate;
  onDelete: (template: EntityTemplate) => void;
  onDuplicate: (template: EntityTemplate) => void;
  onEdit: (template: EntityTemplate) => void;
};

export default function TemplatesTable({
  data,
  isLoading,
  canWrite,
  duplicated,
  onDelete,
  onDuplicate,
  onEdit,
}: TemplatesDataProps) {
  const {t} = useTranslation();
  let columns: Record<
    ColumnsId,
    {
      label: string;
      sortable: boolean;
      sx?: SxProps<Theme>;
    }
  > = {
    name: {
      label: t(translations.office.professional_caregiver.settings.templates.name),
      sortable: false,
      sx: {
        minWidth: '80px',
        maxWidth: '193px',
      },
    },
    area: {
      label: t(translations.office.professional_caregiver.settings.templates.area),
      sortable: false,
      sx: {
        minWidth: '80px',
        maxWidth: '133px',
      },
    },
    description: {
      label: t(translations.office.professional_caregiver.settings.templates.description),
      sortable: false,
      sx: {
        minWidth: '100px',
        maxWidth: {xs: '243px', xl: '443px'},
      },
    },
    update: {
      label: t(translations.office.professional_caregiver.settings.templates.update),
      sortable: false,
      sx: {
        minWidth: '80px',
        maxWidth: {xs: '138px', xl: '138px'},
      },
    },
    buttons: {
      label: '',
      sortable: false,
      sx: canWrite ? undefined : {display: 'none'},
    },
  };

  const columnsData = useMemo(() => {
    const mappedData = data?.map((row) => {
      const activityName = getActivityNameByActivitySectorId(row.activitiesSectorId);
      return {
        name: {value: row.name, row},
        area: {
          value: activityName ? t(translations.role_permissions[activityName]) : '',
          row,
        },
        description: {value: row.description, row},
        update: {
          value: row.updatedAt,
          row,
        },
      };
    });

    return mappedData;
  }, [data, t]);

  return (
    <TableContainer component={Paper} sx={{...tableSx.container, marginTop: '0'}}>
      <Table stickyHeader sx={tableSx.table}>
        <TableHead>
          <TableRow
            sx={{
              [`& .${tableCellClasses.root}`]: {
                ...tableSx.header[`& .${tableCellClasses.root}`],
                fontWeight: '500',
              },
            }}
          >
            {Object.entries(columns).map(([key, {label, sx}], index) => {
              const columnId = key as ColumnsId;
              return (
                <TableCell key={`cell_for_${columnId}_${index}`} sx={sx}>
                  <span className="care-table-header-cell-label">{label}</span>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6}>
                {new Array(6).fill(1).map((_item, index) => (
                  <Skeleton key={`skeleton_${index}`} animation="wave" height={40} />
                ))}
              </TableCell>
            </TableRow>
          ) : (
            columnsData?.map((tableRow) => {
              return (
                <TableRow
                  key={`table_row_${tableRow['name'].row.id}`}
                  sx={{
                    ...tableSx.row,
                    backgroundColor:
                      tableRow['name'].row.id === duplicated?.id
                        ? 'var(--dcm-blue-hover)'
                        : undefined,
                    borderRadius: tableRow['name'].row.id === duplicated?.id ? '5px' : undefined,
                    '& .MuiTableCell-root': {
                      ...tableSx.row['& .MuiTableCell-root'],
                      height: 'auto',
                    },
                    '& .care-table-row-cell': {
                      height: 'auto',
                    },
                  }}
                >
                  {Object.entries(tableRow).map(([key, cell]) => {
                    const columnId = key as ColumnsId;
                    const value =
                      columnId === 'update'
                        ? `${moment(cell.value).format('L')} ${moment(cell.value).format('LT')}`
                        : cell.value
                          ? cell.value
                          : '';

                    return (
                      <TableCell
                        sx={columns[columnId].sx}
                        key={`table_cell_${columnId}`}
                        title={
                          columnId === 'name' || columnId === 'description'
                            ? value.toString()
                            : undefined
                        }
                      >
                        <TextCell value={value.toString()} />
                      </TableCell>
                    );
                  })}
                  <TableCell key="buttons" sx={{position: 'relative'}}>
                    <ComboActions
                      {...{
                        actions: [
                          {
                            label: t(
                              translations.office.professional_caregiver.settings.templates.edit
                            ),
                            icon: {element: FiEdit},
                            disabled: !canWrite,
                            disabledTooltip: t(
                              translations.office.professional_caregiver.settings.templates
                                .no_permissions
                            ),
                            callback: () => onEdit(tableRow['name'].row),
                          },
                          {
                            label: t(
                              translations.office.professional_caregiver.settings.templates.delete
                            ),
                            icon: {element: FiTrash2},
                            disabled: !canWrite,
                            disabledTooltip: t(
                              translations.office.professional_caregiver.settings.templates
                                .no_permissions
                            ),
                            callback: () => onDelete(tableRow['name'].row),
                          },
                          {
                            label: t(
                              translations.office.professional_caregiver.settings.templates
                                .duplicate
                            ),
                            icon: {element: ContentCopyOutlined},
                            disabled: !canWrite,
                            disabledTooltip: t(
                              translations.office.professional_caregiver.settings.templates
                                .no_permissions
                            ),
                            callback: () => onDuplicate(tableRow['name'].row),
                          },
                        ],
                        wrapperSX: {
                          color: 'var(--dcm-gray-light)',
                        },
                        triggerSX: {width: '28px', height: '28px'},
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
